import React, { useEffect, useState } from "react";
import { ShieldCheck, Plus, Trash2, Check, X, Loader2, Lock, ChevronDown, ChevronRight, Pencil } from "lucide-react";
import { Button } from "./ui/button";
import { permissionsApi } from "../lib/api";
import { formatApiErrorDetail } from "../contexts/AuthContext";
import { toast } from "sonner";

const ROLE_BORDER = {
  admin: "border-indigo-300",
  dispatcher: "border-blue-300",
  technician: "border-amber-300",
  office_personnel: "border-emerald-300",
};
const ROLE_DOT = {
  admin: "bg-indigo-500",
  dispatcher: "bg-blue-500",
  technician: "bg-amber-500",
  office_personnel: "bg-emerald-500",
};

const PAYROLL_ACTION_LABELS = {
  edit_hours: "Edit hours",
  edit_commissions: "Edit commissions",
  edit_bonuses: "Edit bonuses/spiffs",
  add_adjustments: "Add other adjustments",
  review_exceptions: "Move periods to review",
  approve: "Approve payroll",
  lock: "Lock payroll",
  reopen: "Reopen locked payroll",
  export: "Export payroll",
  view_audit: "View audit history",
};

/** Editable RBAC: toggle module View/Manage permissions per permission type. */
export default function PermissionTypesCard() {
  const [modules, setModules] = useState([]);
  const [baseRoles, setBaseRoles] = useState([]);
  const [payrollActions, setPayrollActions] = useState([]);
  const [types, setTypes] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    try {
      const [meta, list] = await Promise.all([permissionsApi.modules(), permissionsApi.types()]);
      setModules(meta.modules);
      setBaseRoles(meta.baseRoles);
      setPayrollActions(meta.payrollActions || []);
      setTypes(list);
    } catch {
      toast.error("Failed to load permission types");
      setTypes([]);
    }
  };

  useEffect(() => {
    (async () => { await load(); })();
  }, []);

  const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const hasPerm = (type, key) => (type.permissions || []).includes(key);

  const togglePerm = async (type, key, on) => {
    if (type.id === "admin") return; // admin is locked to all
    let perms = new Set(type.permissions || []);
    const [mod, action] = key.split(".");
    if (on) {
      perms.add(key);
      if (action === "manage") perms.add(`${mod}.view`); // manage implies view
    } else {
      perms.delete(key);
      if (action === "view") perms.delete(`${mod}.manage`); // losing view loses manage
    }
    const next = Array.from(perms);
    setTypes((prev) => prev.map((t) => (t.id === type.id ? { ...t, permissions: next } : t)));
    setSavingId(type.id);
    try {
      await permissionsApi.updateType(type.id, { permissions: next });
    } catch (err) {
      toast.error("Could not update permissions");
      load();
    } finally {
      setSavingId(null);
    }
  };

  const removeType = async (type) => {
    if (!window.confirm(`Delete the "${type.name}" permission type? Users on it revert to their role default.`)) return;
    try {
      await permissionsApi.deleteType(type.id);
      setTypes((prev) => prev.filter((t) => t.id !== type.id));
      toast.success("Permission type deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 mt-6" data-testid="permission-types-card">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4.5 w-4.5 text-indigo-600" />
          <h2 className="text-[15px] font-bold text-slate-900">Permission Types</h2>
        </div>
        <Button onClick={() => setAdding(true)} data-testid="add-permission-type-btn" className="h-8 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[12.5px]">
          <Plus className="h-3.5 w-3.5 mr-1" /> New type
        </Button>
      </div>
      <p className="text-[12px] text-slate-500 mb-4">Choose exactly what each type can see and do. Changes apply immediately to everyone on that type. Admin always has full access.</p>

      {types === null ? (
        <div className="flex justify-center py-10 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : (
        <div className="space-y-2.5">
          {types.map((type) => (
            <TypeRow
              key={type.id}
              type={type}
              modules={modules}
              payrollActions={payrollActions}
              expanded={!!expanded[type.id]}
              saving={savingId === type.id}
              onToggleExpand={() => toggleExpand(type.id)}
              hasPerm={hasPerm}
              onTogglePerm={togglePerm}
              onDelete={() => removeType(type)}
              onRenamed={load}
            />
          ))}
        </div>
      )}

      {adding && (
        <NewTypeModal
          baseRoles={baseRoles}
          onClose={() => setAdding(false)}
          onCreated={(t) => { setAdding(false); setTypes((prev) => [...prev, t]); setExpanded((e) => ({ ...e, [t.id]: true })); }}
        />
      )}
    </div>
  );
}

function TypeRow({ type, modules, payrollActions, expanded, saving, onToggleExpand, hasPerm, onTogglePerm, onDelete, onRenamed }) {
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState(type.name);
  const isAdminType = type.id === "admin";
  const grantedCount = (type.permissions || []).length;

  const saveName = async () => {
    setEditingName(false);
    if (!name.trim() || name === type.name) return;
    try {
      await permissionsApi.updateType(type.id, { name: name.trim() });
      onRenamed();
    } catch {
      toast.error("Rename failed");
      setName(type.name);
    }
  };

  return (
    <div className={`rounded-xl border-2 ${ROLE_BORDER[type.baseRole] || "border-slate-200"} bg-white overflow-hidden`} data-testid={`perm-type-${type.id}`}>
      <div className="flex items-center gap-3 px-3.5 py-3">
        <button onClick={onToggleExpand} data-testid={`perm-type-expand-${type.id}`} className="text-slate-400 hover:text-slate-700">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <span className={`h-2.5 w-2.5 rounded-full ${ROLE_DOT[type.baseRole] || "bg-slate-400"}`} />
        <div className="min-w-0 flex-1">
          {editingName && !type.system ? (
            <input
              autoFocus value={name} onChange={(e) => setName(e.target.value)} onBlur={saveName}
              onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") { setName(type.name); setEditingName(false); } }}
              data-testid={`perm-type-name-input-${type.id}`}
              className="h-7 px-2 rounded border border-blue-300 text-[13.5px] font-semibold focus:outline-none"
            />
          ) : (
            <div className="flex items-center gap-1.5">
              <span className="text-[13.5px] font-bold text-slate-900">{type.name}</span>
              {type.system ? (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-400 uppercase">System</span>
              ) : (
                <button onClick={() => { setName(type.name); setEditingName(true); }} className="text-slate-300 hover:text-slate-600"><Pencil className="h-3 w-3" /></button>
              )}
            </div>
          )}
          <div className="text-[11px] text-slate-400">Base role: {type.baseRole.replace("_", " ")} · {grantedCount} permissions</div>
        </div>
        {saving && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
        {isAdminType && <span className="inline-flex items-center gap-1 text-[11px] text-slate-400"><Lock className="h-3 w-3" /> Full access</span>}
        {!type.system && (
          <button onClick={onDelete} data-testid={`perm-type-delete-${type.id}`} className="h-7 w-7 rounded hover:bg-rose-50 flex items-center justify-center text-slate-400 hover:text-rose-600">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {expanded && (
        <div className="border-t border-slate-100 bg-slate-50/50 px-3.5 py-2">
          <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 items-center text-[11px] font-bold text-slate-400 uppercase tracking-wide px-1 pb-1.5 border-b border-slate-100">
            <span>Module</span><span className="text-center w-12">View</span><span className="text-center w-12">Manage</span>
          </div>
          {modules.map((m) => (
            <React.Fragment key={m.key}>
              <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 items-center px-1 py-1.5">
                <span className="text-[12.5px] text-slate-700">{m.label}</span>
                <PermCheck
                  testid={`perm-${type.id}-${m.key}-view`}
                  checked={hasPerm(type, `${m.key}.view`)}
                  disabled={isAdminType}
                  onChange={(on) => onTogglePerm(type, `${m.key}.view`, on)}
                />
                <PermCheck
                  testid={`perm-${type.id}-${m.key}-manage`}
                  checked={hasPerm(type, `${m.key}.manage`)}
                  disabled={isAdminType}
                  onChange={(on) => onTogglePerm(type, `${m.key}.manage`, on)}
                />
              </div>
              {m.key === "payroll" && payrollActions.length > 0 && (
                <div className="ml-3 mb-1.5 pl-2 border-l-2 border-indigo-100" data-testid={`perm-${type.id}-payroll-actions`}>
                  <div className="text-[10.5px] font-bold text-indigo-400 uppercase tracking-wide px-1 pt-1 pb-0.5">Accountant workflow actions</div>
                  <div className="grid grid-cols-2 gap-x-3">
                    {payrollActions.map((action) => (
                      <label key={action} className="flex items-center gap-2 px-1 py-1 text-[12px] text-slate-600 cursor-pointer">
                        <PermCheck
                          testid={`perm-${type.id}-payroll-${action}`}
                          checked={hasPerm(type, `payroll.${action}`)}
                          disabled={isAdminType}
                          onChange={(on) => onTogglePerm(type, `payroll.${action}`, on)}
                          center={false}
                        />
                        {PAYROLL_ACTION_LABELS[action] || action}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

function PermCheck({ checked, disabled, onChange, testid, center = true }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(!checked)}
      data-testid={testid}
      className={`h-5 w-5 shrink-0 ${center ? "mx-auto" : ""} rounded border flex items-center justify-center transition-colors ${
        checked ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-slate-300 hover:border-blue-400"
      } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
    >
      {checked && <Check className="h-3 w-3" />}
    </button>
  );
}

function NewTypeModal({ baseRoles, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [baseRole, setBaseRole] = useState(baseRoles[1]?.key || "dispatcher");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return setError("Name is required");
    setBusy(true);
    setError("");
    try {
      const t = await permissionsApi.createType({ name: name.trim(), baseRole, permissions: [] });
      onCreated(t);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Could not create type");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-6">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-slate-200" data-testid="new-permission-type-modal">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <div className="text-[14px] font-semibold text-slate-900">New permission type</div>
          <button onClick={onClose} className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center"><X className="h-4 w-4 text-slate-500" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3.5">
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">Name</label>
            <input autoFocus value={name} onChange={(e) => setName(e.target.value)} data-testid="new-perm-type-name" placeholder="e.g. Sales Manager" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">Base role <span className="font-normal text-slate-400">— controls backend access & Staff grouping</span></label>
            <select value={baseRole} onChange={(e) => setBaseRole(e.target.value)} data-testid="new-perm-type-baserole" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-[13.5px] bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20">
              {baseRoles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </div>
          {error && <div className="px-3 py-2 rounded-md bg-rose-50 border border-rose-200 text-[12.5px] text-rose-700">{error}</div>}
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose} className="h-9 text-[13px]">Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="new-perm-type-submit" className="h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[13px]">{busy ? "Creating…" : "Create"}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}
